import * as i0 from '@angular/core';
import { Injectable, Pipe, LOCALE_ID, Inject, InjectionToken, inject, signal, DestroyRef, computed } from '@angular/core';
import { getLocaleFirstDayOfWeek } from '@angular/common';
import { NativeDateAdapter, DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { Subscription } from 'rxjs';
import { first } from 'rxjs/operators';
let ZvExceptionMessageExtractor = /*#__PURE__*/(() => {
  class ZvExceptionMessageExtractor {
    /**
     * Extracts a error message from a given error object
     *
     * @param error The error object.
     * @returns The error message
     */
    extractErrorMessage(error) {
      if (!error) {
        return null;
      }
      if (typeof error === 'string') {
        return error;
      }
      if (typeof error === 'object') {
        return error.message ?? null;
      }
      return null;
    }
    static ɵfac = function ZvExceptionMessageExtractor_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || ZvExceptionMessageExtractor)();
    };
    static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: ZvExceptionMessageExtractor,
      factory: ZvExceptionMessageExtractor.ɵfac,
      providedIn: 'root'
    });
  }
  return ZvExceptionMessageExtractor;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let ZvErrorMessagePipe = /*#__PURE__*/(() => {
  class ZvErrorMessagePipe {
    extractor;
    constructor(extractor) {
      this.extractor = extractor;
    }
    transform(error) {
      return this.extractor.extractErrorMessage(error) ?? '';
    }
    static ɵfac = function ZvErrorMessagePipe_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || ZvErrorMessagePipe)(i0.ɵɵdirectiveInject(ZvExceptionMessageExtractor, 16));
    };
    static ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
      name: "zvErrorMessage",
      type: ZvErrorMessagePipe,
      pure: true
    });
  }
  return ZvErrorMessagePipe;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const INVALID_DATE = new Date(NaN);
const detectDmyOrder = locale => {
  const dateTimeFormat = new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric'
  });
  const dmyOrder = dateTimeFormat.formatToParts(new Date(2222, 5, 1)).map(p => p.type.substring(0, 1)).filter(p => ['d', 'm', 'y'].includes(p));
  return dmyOrder;
};
const dateParserExact$1 = /^(\d+)[./, ](\d+)[./, ](\d+)$/;
const fuzzyRegexMap = {
  d: '(\\d{2})',
  m: '(\\d{2})',
  y: '(\\d{2,4})'
};
const parseHumanDateInput = (currentYear, dmyOrder, value) => {
  if (!value) {
    return null;
  }
  // eslint-disable-next-line @typescript-eslint/no-base-to-string, @typescript-eslint/restrict-template-expressions
  const stringValue = `${value}`.trim();
  const dateParserFuzzy = new RegExp(`^${dmyOrder.map(c => fuzzyRegexMap[c]).join('')}$`);
  const match = stringValue.match(dateParserExact$1) || stringValue.match(dateParserFuzzy);
  if (!match) {
    return INVALID_DATE;
  }
  const dateParts = {
    d: +match[dmyOrder.indexOf('d') + 1],
    m: +match[dmyOrder.indexOf('m') + 1],
    y: match[dmyOrder.indexOf('y') + 1]
  };
  const day = dateParts.d;
  if (day > 31) {
    return INVALID_DATE;
  }
  const month = dateParts.m - 1;
  if (month > 11) {
    return INVALID_DATE;
  }
  let year = +dateParts.y;
  if (dateParts.y.length <= 2) {
    const currentCentury = Math.round(currentYear / 100) * 100;
    const difference = year - (currentYear - currentCentury);
    year = year + currentCentury;
    // If it were more than 10 years in the future with the same century, let's take the last century.
    if (difference > 10) {
      year = year - 100;
    }
  }
  return new Date(year, month, day);
};
const currentYear = new Date().getFullYear();
let ZvNativeDateAdapter = /*#__PURE__*/(() => {
  class ZvNativeDateAdapter extends NativeDateAdapter {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    _dmyOrder = detectDmyOrder(this.locale);
    constructor(_locale) {
      super(_locale);
    }
    getFirstDayOfWeek() {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-deprecated
      return getLocaleFirstDayOfWeek(this.locale);
    }
    // If required extend other NativeDateAdapter methods.
    parse(value) {
      // We have no way using the native JS Date to set the parse format or locale, so we ignore these
      // parameters.
      if (typeof value === 'number') {
        return new Date(value);
      }
      if (value instanceof Date) {
        return new Date(value.getTime());
      }
      return parseHumanDateInput(currentYear, this._dmyOrder, value);
    }
    parseFormatExample() {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      const formattedDate = new Intl.DateTimeFormat(this.locale).format(new Date(2222, 11, 15));
      return formattedDate.replace('2222', 'YYYY').replace('12', 'MM').replace('15', 'DD');
    }
    static ɵfac = function ZvNativeDateAdapter_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || ZvNativeDateAdapter)(i0.ɵɵinject(LOCALE_ID));
    };
    static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: ZvNativeDateAdapter,
      factory: ZvNativeDateAdapter.ɵfac,
      providedIn: 'root'
    });
  }
  return ZvNativeDateAdapter;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const ZV_NATIVE_DATE_FORMATS = {
  parse: {
    dateInput: null
  },
  display: {
    dateInput: {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    },
    monthYearLabel: {
      year: 'numeric',
      month: 'short'
    },
    dateA11yLabel: {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    },
    monthYearA11yLabel: {
      year: 'numeric',
      month: 'long'
    }
  }
};
const dateParserExact = /^(\d{0,2})[^0-9](\d{0,2})([^0-9]*)$/;
const dateParserFuzzy3to4 = /^(\d{1,2})(\d{2})([^0-9]*)$/;
const dateParserFuzzy2 = /^(\d{1,2})()([^0-9]*)$/;
const parseHumanTimeInput = value => {
  if (!value) {
    return null;
  }
  // eslint-disable-next-line @typescript-eslint/no-base-to-string, @typescript-eslint/restrict-template-expressions
  const stringValue = `${value}`.trim();
  const match = stringValue.match(dateParserExact) || stringValue.match(dateParserFuzzy3to4) || stringValue.match(dateParserFuzzy2);
  if (!match || match[1] === '' && match[2] === '') {
    return NaN;
  }
  const minutes = +match[2];
  if (minutes > 59 || minutes < 0) {
    return NaN;
  }
  let hours = +match[1];
  const ampm = match[3].replace(/[^pamPAM]/g, '').toLocaleLowerCase();
  if (hours > 23 || hours < 0 || ampm && hours > 12) {
    return NaN;
  }
  if (ampm === 'pm' && hours < 12) {
    hours += 12;
  }
  if (ampm === 'am' && hours == 12) {
    hours -= 12;
  }
  return [hours, minutes];
};
let ZvTimeAdapter = /*#__PURE__*/(() => {
  class ZvTimeAdapter {
    /**
     * Given a potential date object, returns that same date object if it is
     * a valid date, or `null` if it's not a valid date.
     * @param obj The object to check.
     * @returns A date or `null`.
     */
    getValidTimeOrNull(obj) {
      return this.isTimeInstance(obj) && this.isValid(obj) ? obj : null;
    }
    /**
     * Attempts to deserialize a value to a valid time object. This is different from parsing in that
     * deserialize should only accept non-ambiguous, locale-independent formats (e.g. a ISO 8601
     * string). The default implementation does not allow any deserialization, it simply checks that
     * the given value is already a valid time object or null. The `<zv-date-time-input>` will call this
     * method on all of its `@Input()` properties that accept times. It is therefore possible to
     * support passing values from your backend directly to these properties by overriding this method
     * to also deserialize the format used by your backend.
     * @param value The value to be deserialized into a time object.
     * @returns The deserialized time object, either a valid time, null if the value can be
     *     deserialized into a null time (e.g. the empty string), or an invalid time.
     */
    deserialize(value) {
      if (!value) {
        return null;
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      if (this.isTimeInstance(value) && this.isValid(value)) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return value;
      }
      if (typeof value === 'string') {
        const matches = value.match(/^(\d{2}):(\d{2})$/);
        if (matches) {
          return this.createTime(+matches[1], +matches[2]);
        }
      }
      return this.invalid();
    }
    /**
     * Checks if two times are equal.
     * @param first The first time to check.
     * @param second The second time to check.
     * @returns Whether the two times are equal.
     *     Null times are considered equal to other null times.
     */
    sameTime(first, second) {
      if (first && second) {
        const firstValid = this.isValid(first);
        const secondValid = this.isValid(second);
        if (firstValid && secondValid) {
          return !this.compareTime(first, second);
        }
        return firstValid == secondValid;
      }
      return first == second;
    }
    static ɵfac = function ZvTimeAdapter_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || ZvTimeAdapter)();
    };
    static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: ZvTimeAdapter,
      factory: ZvTimeAdapter.ɵfac,
      providedIn: 'root'
    });
  }
  return ZvTimeAdapter;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let ZvNativeTimeAdapter = /*#__PURE__*/(() => {
  class ZvNativeTimeAdapter extends ZvTimeAdapter {
    locale;
    constructor(locale) {
      super();
      this.locale = locale;
    }
    _is24HourFormat = null;
    is24HourFormat() {
      if (this._is24HourFormat === null) {
        const hourCycle = new Intl.DateTimeFormat(this.locale, {
          hour: 'numeric'
        }).resolvedOptions().hourCycle ?? '';
        this._is24HourFormat = ['h23', 'h24'].includes(hourCycle);
      }
      return this._is24HourFormat;
    }
    createTime(hour, minute) {
      // use ngDevMode, when it is exposed: https://github.com/angular/angular-cli/issues/23738
      if (hour < 0 || hour > 23) {
        throw Error(`Invalid hour "${hour}". Hour has to be between 0 and 23.`);
      }
      if (minute < 0 || minute > 59) {
        throw Error(`Invalid minute "${minute}". Minute has to be between 0 and 59.`);
      }
      return {
        hours: hour,
        minutes: minute
      };
    }
    parse(value) {
      if (this.isTimeInstance(value)) {
        return this.createTime(value.hours, value.minutes);
      }
      const time = parseHumanTimeInput(value);
      if (time == null) {
        return null;
      }
      if (Array.isArray(time)) {
        return this.createTime(time[0], time[1]);
      }
      return this.invalid();
    }
    format(time, displayFormat = null) {
      if (!time) {
        return '';
      }
      if (isNaN(time.hours) || isNaN(time.minutes)) {
        throw new Error('ZvNativeTimeAdapter: Cannot format invalid Time.');
      }
      displayFormat ??= {};
      if (typeof displayFormat !== 'object') {
        throw new Error('ZvNativeTimeAdapter: displayFormat must be of type Intl.DateTimeFormatOptions.');
      }
      return new Intl.DateTimeFormat(this.locale, {
        hour12: !this.is24HourFormat(),
        hour: '2-digit',
        minute: '2-digit',
        ...displayFormat
      }).format(new Date(2000, 1, 1, time.hours, time.minutes));
    }
    isTimeInstance(obj) {
      return obj !== null && typeof obj == 'object' && 'hours' in obj && 'minutes' in obj;
    }
    isValid(time) {
      return !isNaN(time.minutes) && !isNaN(time.hours);
    }
    invalid() {
      return {
        hours: NaN,
        minutes: NaN
      };
    }
    compareTime(first, second) {
      return first.hours - second.hours || first.minutes - second.minutes;
    }
    parseFormatExample() {
      const formattedDate = new Intl.DateTimeFormat(this.locale, {
        hour: '2-digit',
        minute: '2-digit'
      }).format(new Date(0, 0, 0, 11, 30));
      return formattedDate.replace('11', 'hh').replace('30', 'mm');
    }
    static ɵfac = function ZvNativeTimeAdapter_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || ZvNativeTimeAdapter)(i0.ɵɵinject(LOCALE_ID));
    };
    static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: ZvNativeTimeAdapter,
      factory: ZvNativeTimeAdapter.ɵfac,
      providedIn: 'root'
    });
  }
  return ZvNativeTimeAdapter;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const ZV_NATIVE_TIME_FORMATS = {
  parse: {
    timeInput: null
  },
  display: {
    timeInput: {
      hour: '2-digit',
      minute: '2-digit'
    }
  }
};
const ZV_TIME_FORMATS = new InjectionToken('ZV_TIME_FORMATS');
let ZvDateAdapter = /*#__PURE__*/(() => {
  class ZvDateAdapter extends DateAdapter {
    static ɵfac = /* @__PURE__ */(() => {
      let ɵZvDateAdapter_BaseFactory;
      return function ZvDateAdapter_Factory(__ngFactoryType__) {
        return (ɵZvDateAdapter_BaseFactory || (ɵZvDateAdapter_BaseFactory = i0.ɵɵgetInheritedFactory(ZvDateAdapter)))(__ngFactoryType__ || ZvDateAdapter);
      };
    })();
    static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: ZvDateAdapter,
      factory: ZvDateAdapter.ɵfac,
      providedIn: 'root'
    });
  }
  return ZvDateAdapter;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let ZvDateTimeAdapter = /*#__PURE__*/(() => {
  class ZvDateTimeAdapter {
    dateAdapter = inject(ZvDateAdapter);
    timeAdapter = inject(ZvTimeAdapter);
    static ɵfac = function ZvDateTimeAdapter_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || ZvDateTimeAdapter)();
    };
    static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: ZvDateTimeAdapter,
      factory: ZvDateTimeAdapter.ɵfac,
      providedIn: 'root'
    });
  }
  return ZvDateTimeAdapter;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
function provideDateTimeAdapters(dateTimeAdapter, dateAdapter, timeAdapter) {
  return [{
    provide: ZvDateTimeAdapter,
    useClass: dateTimeAdapter
  }, {
    provide: ZvDateAdapter,
    useClass: dateAdapter
  }, {
    provide: DateAdapter,
    useExisting: ZvDateAdapter
  }, {
    provide: ZvTimeAdapter,
    useClass: timeAdapter
  }];
}
function provideDateTimeFormats(dateFormats, timeFormats) {
  return [{
    provide: ZV_TIME_FORMATS,
    useValue: timeFormats
  }, {
    provide: MAT_DATE_FORMATS,
    useValue: dateFormats
  }];
}
let ZvNativeDateTimeAdapter = /*#__PURE__*/(() => {
  class ZvNativeDateTimeAdapter extends ZvDateTimeAdapter {
    splitDateTime(date) {
      if (!date) {
        return {
          date: null,
          time: null
        };
      }
      return {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        date: this.dateAdapter.createDate(date.getFullYear(), date.getMonth(), date.getDate()),
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        time: this.timeAdapter.createTime(date.getHours(), date.getMinutes())
      };
    }
    mergeDateTime(date, time) {
      if (date === null) {
        return null;
      }
      if (!this.dateAdapter.isValid(date) || time != null && !this.timeAdapter.isValid(time)) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return this.dateAdapter.invalid();
      }
      const newValue = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      if (time != null) {
        newValue.setHours(time.hours);
        newValue.setMinutes(time.minutes);
      }
      return newValue;
    }
    static ɵfac = /* @__PURE__ */(() => {
      let ɵZvNativeDateTimeAdapter_BaseFactory;
      return function ZvNativeDateTimeAdapter_Factory(__ngFactoryType__) {
        return (ɵZvNativeDateTimeAdapter_BaseFactory || (ɵZvNativeDateTimeAdapter_BaseFactory = i0.ɵɵgetInheritedFactory(ZvNativeDateTimeAdapter)))(__ngFactoryType__ || ZvNativeDateTimeAdapter);
      };
    })();
    static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: ZvNativeDateTimeAdapter,
      factory: ZvNativeDateTimeAdapter.ɵfac,
      providedIn: 'root'
    });
  }
  return ZvNativeDateTimeAdapter;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
class ZvActionDataSource {
  options;
  #state = signal({
    state: 'idle',
    error: null
  });
  #destroyRef = inject(DestroyRef);
  #actionSub = Subscription.EMPTY;
  constructor(options) {
    this.options = options;
    this.#destroyRef.onDestroy(() => this.#actionSub.unsubscribe());
  }
  error = computed(() => this.#state().error);
  isLoading = computed(() => this.#state().state == 'loading');
  hasError = computed(() => this.#state().state == 'error');
  succeeded = computed(() => this.#state().state == 'success');
  execute() {
    this.#actionSub.unsubscribe();
    this.#state.set({
      error: null,
      state: 'loading'
    });
    let load$ = this.options.actionFn();
    load$ = load$.pipe(first());
    this.#actionSub = load$.subscribe({
      next: () => {
        this.#state.set({
          error: null,
          state: 'success'
        });
      },
      error: err => {
        this.#state.set({
          error: err,
          state: 'error'
        });
      }
    });
  }
}

// export what ./public_api exports so we can import with the lib name like this:
// import { ModuleA } from 'libname'

/**
 * Generated bundle index. Do not edit.
 */

export { ZV_NATIVE_DATE_FORMATS, ZV_NATIVE_TIME_FORMATS, ZV_TIME_FORMATS, ZvActionDataSource, ZvDateTimeAdapter, ZvErrorMessagePipe, ZvExceptionMessageExtractor, ZvNativeDateAdapter, ZvNativeDateTimeAdapter, ZvNativeTimeAdapter, ZvTimeAdapter, provideDateTimeAdapters, provideDateTimeFormats };
